import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { useTheme } from '@material-ui/core/styles';

import { handleSortChange } from 'modules/common';
import {
  makeRowColumn,
  getHeadObj,
  converParameters,
  getHost,
} from 'lib/commonLib';
import { budgetCategoryList, budgetTargeting } from 'lib/api/budget';

import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import SortLabelWrap from 'components/molecules/MbTable/SortLabelWrap';
import SpanningTableContents from 'components/molecules/MbTable/SpanningTableContents';
import SwitchingArea from 'components/organisms/SwitchingArea';
import FilterCollection from 'components/organisms/FilterCollection';

import BudgetCommonFilter from './BudgetCommonFilter';
import StatisticsGraph from './StatisticsGraph';

const Statistics = (props) => {
  const { handlePortalUrl, productList, categoryList, handleClickSnackbar } =
    props;
  const { t } = useTranslation();
  const theme = useTheme();

  let initCustomized = {
    FIXED: { merged: true },
    CTGR_SEQ: { merged: true },
    STATS_DTTM: { merged: true },
    ADVRTS_TP_CODE_STR: { orderFlag: true },
    DD_EXHS_AMT: {
      makeHead: (headsMap) => {
        let toolTip = '';
        toolTip = (
          <>
            ● {t('카테고리 별 총 예산 금액 영역')}
            <br />● {t('최상단 통계 영역 : 전체 카테고리의 총 예산 금액 노출')}
            <br />● {t('예산금액데이터')}
            <br />: {t('1일전 배치돈 시점의 예산금액 (전일 오후 11시)')}
          </>
        );
        return (
          <InfoTitleArea
            title={getHeadObj(headsMap, 'DD_EXHS_AMT').label}
            tooltipTitle={toolTip}
            iconType="fainfoCircle"
          />
        );
      },
      orderFlag: true,
    },
  };

  const [initialization, setInitialization] = useState(false);
  const initParams = {
    sort: 'desc',
    order: 'DD_EXHS_RATE',
    statsDttm: moment().format('YYYYMMDD'),
    ctgrSeqs: '2,3,7,11,14,16,17,18,19,20,22,24,588,611,618,634',
    stdCode: '01',
    pageType: '01',
  };
  const [params, setParams] = useState(initParams);
  const [customized, setCustomized] = useState(initCustomized);
  const [infoMessage, setInfoMessage] = useState('');
  const [reFetch, setReFetch] = useState(false);

  useEffect(() => {
    const eDate = moment(params['statsDttm'])
      .add(-2, 'day')
      .format('YYYY.MM.DD');
    const sDate = moment(eDate).add(-1, 'year').format('YYYY.MM.DD');
    handleCustom(params);
    setInfoMessage(
      <>
        최종 업데이트일 : {moment(params['statsDttm']).format('YYYY.MM.DD')}
        <br />
        데이터 통계 기간 : {sDate} ~{eDate}
      </>
    );
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
    if (reFetch) {
      setReFetch(false);
    }
    // eslint-disable-next-line
  }, [initialization, reFetch]);

  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
      handleCustom(initParams);
    }
  };

  const getParam = (newParams, name) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      let tempParams = { ...newParams };
      if (name === 'pageType') {
        if (newParams[name] === '01') {
          tempParams = {
            ...tempParams,
            sort: 'asc',
            order: 'DD_EXHS_RATE',
            uri: '/report/budget/targetingDetail',
          };
          delete tempParams['uri'];
        } else if (newParams[name] === '02') {
          tempParams = {
            ...tempParams,
            sort: 'asc',
            order: 'ADVRTS_TP_CODE_STR',
            uri: '/report/budget/targetingDetail',
          };
        }
        setReFetch(true);
      }
      setParams(tempParams);
      handleCustom(tempParams);
    }
  };

  const handleCustom = (propsParams) => {
    let newCustomized = {
      ...initCustomized,
      CTGR_NM: { merged: true },
      DD_EXHS_RATE: {
        makeHead: (headsMap) => {
          return `${getHeadObj(headsMap, 'CTGR_NM').label}`;
        },
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'CTGR_NM');
          const bodyItem2 = getHeadObj(headsMap, 'DD_EXHS_RATE');
          let result;
          const showWindowUrl = converParameters({
            url: getHost('new') + '/report/budget/share/detail',
            statsDttm: propsParams.statsDttm,
            ctgrSeqs: propsParams.ctgrSeqs,
            stdCode: propsParams.stdCode,
            uri: '/report/budget/categoryDetail',
            isPop: 1,
          });
          if (rowObj['FIXED'] !== 0) {
            result = (
              <>
                {makeRowColumn(
                  rowObj['CTGR_NM'],
                  bodyItem1.type,
                  bodyItem1.format
                )}
                (
                {makeRowColumn(
                  rowObj['DD_EXHS_RATE'],
                  bodyItem2.type,
                  bodyItem2.format
                )}
                )
              </>
            );
          } else {
            result = (
              <IconAreaWrap
                text={makeRowColumn(
                  rowObj['CTGR_NM'],
                  bodyItem1.type,
                  bodyItem1.format
                )}
                icon="faExternalLink"
                toolTipTitle={t('일자별 예산 비중도')}
                onClick={(e) => handlePortalUrl(showWindowUrl)}
              />
            );
          }
          return result;
        },
        orderFlag: true,
      },
    };
    for (let key of productList) {
      const styleObj =
        key === 'BANNER'
          ? {
              borderLeft: '1px solid #e5e8eb',
              borderRight: '1px solid #e5e8eb',
            }
          : { borderRight: '1px solid #e5e8eb' };
      newCustomized[key + '_WEB_CNST'] = {
        makeHead: (headsMap) => {
          let title = '';
          let toolTip = '';
          if (key === 'BANNER') {
            title = t('배너');
          } else if (key === 'ICOVER') {
            title = t('아이커버');
          } else if (key === 'NATIVE') {
            title = t('네이티브');
          }
          if (headsMap.has('CTGR_NM')) {
            toolTip = (
              <>
                ●{' '}
                {t('예산비중도 tooltip 1', {
                  date: title,
                  name: t('카테고리'),
                })}
                <br />● {t('비중도')} : PC+MO =100%
                <br />● {t('예산비중도 category tooltip 2', { date: title })}
              </>
            );
          } else {
            toolTip = (
              <>
                ●{' '}
                {t('예산비중도 tooltip 1', {
                  date: title,
                  name: t('타게팅'),
                })}
                <br />● {t('비중도')}
                <br /> -{' '}
                {t('예산비중도 targeting tooltip 2', {
                  date: title,
                })}{' '}
                = 100%
                <br /> -{' '}
                {t('예산비중도 targeting tooltip 3', {
                  date: title,
                })}{' '}
                = 100%
              </>
            );
          }
          return (
            <SpanningTableContents
              topTitle={
                <InfoTitleArea
                  title={title}
                  tooltipTitle={toolTip}
                  iconType="fainfoCircle"
                />
              }
              type="head"
            >
              <div style={{ width: 50 }}>
                <SortLabelWrap
                  params={propsParams}
                  label="PC"
                  orderKey={key + '_WEB_CNST'}
                  onClick={(e) =>
                    getParam(handleSortChange(propsParams, key + '_WEB_CNST'))
                  }
                />
              </div>
              <div style={{ width: 50 }}>
                <SortLabelWrap
                  params={propsParams}
                  label="Mobile"
                  orderKey={key + '_MOBILE_CNST'}
                  onClick={(e) =>
                    getParam(
                      handleSortChange(propsParams, key + '_MOBILE_CNST')
                    )
                  }
                />
              </div>
            </SpanningTableContents>
          );
        },
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, key + '_WEB_CNST');
          const bodyItem2 = getHeadObj(headsMap, key + '_MOBILE_CNST');
          const naText = (
            <span style={{ color: theme.palette.common.gray2 }}>(N/A)</span>
          );
          const isTotalRow = rowObj['FIXED'] === 0;
          return (
            <SpanningTableContents>
              <div style={{ width: 50 }}>
                {rowObj[key + '_WEB_CNST'] !== null
                  ? makeRowColumn(
                      rowObj[key + '_WEB_CNST'],
                      bodyItem1.type,
                      isTotalRow ? '0.00%' : bodyItem1.format
                    )
                  : naText}
              </div>
              <div style={{ width: 50 }}>
                {rowObj[key + '_MOBILE_CNST'] !== null
                  ? makeRowColumn(
                      rowObj[key + '_MOBILE_CNST'],
                      bodyItem2.type,
                      isTotalRow ? '0.00%' : bodyItem2.format
                    )
                  : naText}
              </div>
            </SpanningTableContents>
          );
        },
        orderFlag: false,
        headStyle: styleObj,
        bodyStyle: styleObj,
      };
      newCustomized[key + '_MOBILE_CNST'] = { merged: true };
    }
    setCustomized(newCustomized);
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <BudgetCommonFilter
          params={params}
          getParam={getParam}
          handleOnReset={handleOnReset}
          categoryList={categoryList}
          initialization={initialization}
        />
        <FilterCollection
          label={t('상수')}
          name="pageType"
          params={params}
          value={params.pageType}
          type="radio"
          list={[
            { label: t('카테고리'), value: '01' },
            { label: t('타게팅'), value: '02' },
          ]}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
      </NewFilterWrap>
      <StatisticsGraph
        params={params}
        handleClickSnackbar={handleClickSnackbar}
      />
      <SwitchingArea
        title={
          <InfoTitleArea
            title={
              params['pageType'] === '01'
                ? t('카테고리 별 비중도')
                : t('타게팅 별 비중도')
            }
            tooltipTitle={infoMessage}
            iconType="fainfoCircle"
            iconColor="graph"
          />
        }
        graphData
      >
        <NewTableContainer
          params={params}
          getParam={getParam}
          dataReqPromise={
            params['pageType'] === '01' ? budgetCategoryList : budgetTargeting
          }
          customized={customized}
          isIncludingTotal={params['pageType'] === '01'}
          useTotal={params['pageType'] === '01'}
          useFetch={false}
          useSearch={false}
          useStoreLoading={false}
          useFullScreen={false}
          usePagenation={false}
          rowsPerPage={-1}
          reFetchHead
          reFetch={reFetch}
          paramsRemoval={['pageType']}
        />
      </SwitchingArea>
    </>
  );
};

export default Statistics;
